import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { getPublicWorkflow } from "api-client";
import { createContext, useContext } from "react";
import { assert } from "utils";

import { loaderHandler } from "src/lib/loaderHandler";

interface PublicWorkflowData {
  id: string;
  name: string;
  currentUserEnrollment: {
    id: string;
    status: string;
    enrolledAt: string | null;
  } | null;
}

interface PublicWorkflowContextValue {
  workflow: PublicWorkflowData;
}

const PublicWorkflowContext = createContext<PublicWorkflowContextValue | null>(
  null,
);

export function usePublicWorkflowContext() {
  const context = useContext(PublicWorkflowContext);
  assert(
    context,
    "usePublicWorkflowContext must be used within PublicWorkflowProvider",
  );
  return context;
}

export const Route = createFileRoute("/_hybrid/workflow/$workflowId")({
  component: RoutePage,
  ssr: true,
  async loader({ params }) {
    return await loaderHandler(async () => {
      try {
        assert(params.workflowId, "No workflow ID provided");
        const response = await getPublicWorkflow({
          path: { workflowId: params.workflowId },
        });
        return {
          data: {
            workflow: response.data.data,
          },
        };
      } catch {
        throw redirect({
          to: "/",
        });
      }
    });
  },
});

function RoutePage() {
  const loaderData = Route.useLoaderData();
  const workflow = loaderData.data.workflow;

  return (
    <PublicWorkflowContext.Provider value={{ workflow }}>
      <Outlet />
    </PublicWorkflowContext.Provider>
  );
}
